import { storeClosedModalAtom } from "@/atoms/store-closed-modal"
import { useSetAtom } from "jotai"
import { useCallback } from "react"
import { useMerchantStatus } from "./use-merchant-status"
import { useStorefrontScheduling } from "./use-storefront-scheduling"

/**
 * Whether the customer may hold a bag at all: the channel is open right now, or
 * it is closed but still takes a scheduled order (R7). Every bag affordance —
 * adding an item, and reaching the bag afterwards — answers to this, so a bag
 * can never exist without a way back to it.
 */
export function useBagUnlocked(): boolean {
  const { merchantIsOpen } = useMerchantStatus()
  const scheduling = useStorefrontScheduling()
  return merchantIsOpen || (scheduling.known && scheduling.bagUnlocked)
}

/**
 * Guards "add to bag" against a closed channel. With `?table=` the channel is
 * INDOOR; otherwise DELIVERY (see useMerchantStatus). Mirrors iFood: when
 * closed, block the addition and show a bottom-sheet — browse stays free.
 *
 * A closed store whose channel still takes scheduled orders is NOT blocked
 * (R7): the bag is free and the customer picks a slot at checkout. A target
 * window with no slots left, or no window at all, blocks as before.
 */
export function useStoreClosedGuard() {
  const canAddToBag = useBagUnlocked()
  const openStoreClosedModal = useSetAtom(storeClosedModalAtom)

  const ensureStoreOpen = useCallback(() => {
    if (!canAddToBag) {
      openStoreClosedModal(true)
      return false
    }
    return true
  }, [canAddToBag, openStoreClosedModal])

  return { merchantIsOpen: canAddToBag, ensureStoreOpen }
}
